import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calculator,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Modal,
  PageHeader,
  Spinner,
  useToast,
} from '../../ui';
import { useEstimates } from '../../features/estimation/useEstimates';
import { useTemplates } from '../../features/templates/useTemplates';
import { useRooms } from '../../features/rooms/useRooms';
import type {
  Estimate,
  EstimateInputValue,
} from '../../features/estimation/types';
import type { OutputDef, Template } from '../../features/templates/types';
import { OutputsPreview } from './OutputsPreview';

/** Formats a raw input value for read-only display. */
function formatInput(value: EstimateInputValue): string {
  if (typeof value === 'number') {
    return Number.isFinite(value)
      ? value.toLocaleString(undefined, { maximumFractionDigits: 4 })
      : '—';
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value === '' ? '—' : value;
}

/**
 * Estimate detail — a read-only view of a saved estimate: its template, room,
 * inputs and computed outputs. Supports re-opening the estimate in the wizard
 * (as a clone) and soft-deleting it.
 *
 * The estimate's outputs are the values computed and stored at save time; this
 * page does not recompute them. Field/output labels are resolved best-effort
 * from the referenced template (which may be a newer version or soft-deleted).
 */
export function EstimateDetailPage() {
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId ? decodeURIComponent(rawId) : '';
  const navigate = useNavigate();
  const { toast } = useToast();

  const estimates = useEstimates();
  const templates = useTemplates();
  const rooms = useRooms();

  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    estimates
      .getById(id)
      .then(async (e) => {
        if (cancelled) return;
        if (!e || e.deleted) {
          setEstimate(null);
          setError('Estimate not found.');
          return;
        }
        setEstimate(e);
        // Best-effort template hydration for labels/output defs.
        try {
          const t = await templates.getById(e.templateId);
          if (!cancelled) setTemplate(t ?? null);
        } catch {
          if (!cancelled) setTemplate(null);
        }
      })
      .catch((err) => {
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : 'Failed to load estimate.',
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, estimates.getById, templates.getById]);

  const roomName = useMemo(() => {
    if (!estimate?.roomId) return null;
    return (
      rooms.summaries.find((r) => r.id === estimate.roomId)?.name ??
      estimate.roomId
    );
  }, [estimate, rooms.summaries]);

  // Output defs: prefer the template's (labels + declaration order), else
  // synthesize from the stored output keys so we can still render them.
  const outputDefs = useMemo<OutputDef[]>(() => {
    if (template?.outputs && template.outputs.length > 0) return template.outputs;
    return Object.keys(estimate?.outputs ?? {}).map((key) => ({
      key,
      label: key,
      expression: '',
    }));
  }, [template, estimate]);

  // Input rows: prefer the template's field order/labels, else the stored keys.
  const inputRows = useMemo(() => {
    if (!estimate) return [];
    if (template && template.fields.length > 0) {
      return template.fields.map((f) => ({
        key: f.key,
        label: f.label || f.key,
        value: estimate.inputs[f.key],
      }));
    }
    return Object.entries(estimate.inputs).map(([key, value]) => ({
      key,
      label: key,
      value,
    }));
  }, [estimate, template]);

  const handleReopen = () => {
    if (!estimate) return;
    navigate('/estimation/new', { state: { cloneFrom: estimate } });
  };

  const handleDelete = async () => {
    if (!estimate) return;
    setDeleting(true);
    try {
      await estimates.remove(estimate.id);
      toast({
        tone: 'success',
        title: 'Estimate deleted',
        description: estimate.title,
      });
      navigate('/estimation');
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Delete failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading estimate" />
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div>
        <PageHeader
          icon={<Calculator className="h-5 w-5" />}
          title="Estimate"
          actions={
            <Button
              variant="ghost"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/estimation')}
            >
              Back
            </Button>
          }
        />
        <EmptyState
          icon={<Calculator className="h-6 w-6" />}
          title="Estimate unavailable"
          description={error ?? 'This estimate could not be found.'}
        />
      </div>
    );
  }

  const created = new Date(estimate.createdAt);
  const createdLabel = Number.isNaN(created.getTime())
    ? estimate.createdAt
    : created.toLocaleString();

  return (
    <div>
      <PageHeader
        icon={<Calculator className="h-5 w-5" />}
        title={estimate.title}
        description={
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <Badge tone="accent">
              {template ? `${template.name} v${estimate.templateVersion}` : estimate.templateId}
            </Badge>
            {roomName && <Badge tone="neutral">{roomName}</Badge>}
            <span className="text-zinc-500">
              by {estimate.createdBy} · {createdLabel}
            </span>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/estimation')}
            >
              Back
            </Button>
            <Button
              variant="secondary"
              leftIcon={<RotateCcw className="h-4 w-4" />}
              onClick={handleReopen}
            >
              Re-open
            </Button>
            <Button
              variant="danger"
              leftIcon={<Trash2 className="h-4 w-4" />}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Outputs</CardTitle>
          </CardHeader>
          <CardBody>
            <OutputsPreview
              defs={outputDefs}
              result={{ outputs: estimate.outputs, errors: {} }}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Inputs</CardTitle>
          </CardHeader>
          <CardBody>
            {inputRows.length === 0 ? (
              <p className="text-sm text-zinc-500">No inputs recorded.</p>
            ) : (
              <dl className="divide-y divide-surface-border">
                {inputRows.map((row) => (
                  <div
                    key={row.key}
                    className="flex items-center justify-between gap-4 py-2"
                  >
                    <dt className="text-sm text-zinc-400">{row.label}</dt>
                    <dd className="text-sm font-medium tabular-nums text-zinc-100">
                      {row.value === undefined ? '—' : formatInput(row.value)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        open={confirmDelete}
        onClose={() => !deleting && setConfirmDelete(false)}
        size="sm"
        title="Delete estimate"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button variant="danger" loading={deleting} onClick={handleDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-400">
          Delete{' '}
          <span className="font-medium text-zinc-200">{estimate.title}</span>?
          This can't be undone from the UI.
        </p>
      </Modal>
    </div>
  );
}

export default EstimateDetailPage;
